import { Button } from '@tark/shared/components';
import type { CompetitionOrganizerEntry } from '@tark/shared/lib';
import { Check, MoreHorizontal, UserCheck, UserMinus, UsersRound } from 'lucide-react';
import type { OrganizerEntryIntent } from './organizerCompetitionUi';
import {
  formatCompetitionCode,
  formatCompetitionDate,
  organizerStatusClass,
} from './organizerCompetitionUi';

type OrganizerEntrantTableProps = {
  entries: CompetitionOrganizerEntry[];
  timeZone?: string | null;
  canAdmin: boolean;
  readOnly: boolean;
  actionEntryId: string | null;
  onReview: (entry: CompetitionOrganizerEntry, action: 'approve' | 'reserve') => void;
  onIntent: (intent: OrganizerEntryIntent) => void;
};

export default function OrganizerEntrantTable({
  entries,
  timeZone,
  canAdmin,
  readOnly,
  actionEntryId,
  onReview,
  onIntent,
}: OrganizerEntrantTableProps) {
  if (entries.length === 0) {
    return (
      <div className="px-4 py-14 text-center">
        <UsersRound className="mx-auto h-9 w-9 text-text-subtle" />
        <p className="mt-3 font-semibold text-text">No matching entrants</p>
        <p className="mt-1 text-sm text-text-muted">Change the search or filters to see the full field.</p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border bg-surface-elevated/30 text-xs font-semibold uppercase text-text-muted">
              <th className="px-4 py-3">Entrant</th>
              <th className="px-4 py-3">Division</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.entryPublicId} className="border-b border-border last:border-0">
                <td className="px-4 py-4 align-top"><EntrantIdentity entry={entry} /></td>
                <td className="px-4 py-4 align-top">
                  <p className="text-sm font-semibold text-text">{entry.divisionName}</p>
                  <p className="mt-1 text-xs text-text-muted">
                    {formatCompetitionCode(entry.formatCode)} · {entry.entryType === 'FIXED_PAIR' ? 'Fixed pair' : 'Singles'}
                  </p>
                </td>
                <td className="px-4 py-4 align-top"><StatusPill entry={entry} /></td>
                <td className="px-4 py-4 align-top text-sm text-text-muted">
                  {formatCompetitionDate(entry.requestedAt, timeZone)}
                </td>
                <td className="px-4 py-4 align-top">
                  <div className="flex justify-end gap-2">
                    <QuickActions
                      entry={entry}
                      canAdmin={canAdmin}
                      readOnly={readOnly}
                      isPending={actionEntryId === entry.entryPublicId}
                      onReview={onReview}
                      onIntent={onIntent}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-border md:hidden">
        {entries.map((entry) => (
          <article key={entry.entryPublicId} className="space-y-3 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 basis-40"><EntrantIdentity entry={entry} /></div>
              <StatusPill entry={entry} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-text-muted">
              <div>
                <p className="font-semibold text-text">{entry.divisionName}</p>
                <p className="mt-1">{formatCompetitionCode(entry.formatCode)}</p>
              </div>
              <div className="text-right">
                <p>Requested</p>
                <p className="mt-1 font-medium text-text">{formatCompetitionDate(entry.requestedAt, timeZone)}</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <QuickActions
                entry={entry}
                canAdmin={canAdmin}
                readOnly={readOnly}
                isPending={actionEntryId === entry.entryPublicId}
                onReview={onReview}
                onIntent={onIntent}
              />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function EntrantIdentity({ entry }: { entry: CompetitionOrganizerEntry }) {
  return (
    <div className="min-w-0">
      <p className="font-semibold text-text">
        {entry.members.map((member) => member.displayName).join(' & ') || 'Unnamed entrant'}
      </p>
      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-text-muted">
        {entry.members.map((member) => (
          <span key={member.entrantPublicId}>
            {[member.levelLabel, member.homeArea].filter(Boolean).join(' · ') || 'Profile details pending'}
            {' · '}{member.linkedAccount ? 'Linked' : 'Claim needed'}
            {member.sourceCode ? ` · ${formatCompetitionCode(member.sourceCode)}` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ entry }: { entry: CompetitionOrganizerEntry }) {
  return (
    <div className="flex max-w-full shrink-0 flex-col items-start gap-1">
      <span className={`max-w-full break-normal rounded-full border px-2.5 py-1 text-xs font-semibold ${organizerStatusClass(entry.status)}`}>
        {formatCompetitionCode(entry.status)}
        {entry.reservePosition ? ` #${entry.reservePosition}` : ''}
      </span>
      {entry.decisionReason ? <span className="max-w-44 text-xs text-text-muted">{entry.decisionReason}</span> : null}
    </div>
  );
}

function QuickActions({
  entry,
  canAdmin,
  readOnly,
  isPending,
  onReview,
  onIntent,
}: {
  entry: CompetitionOrganizerEntry;
  canAdmin: boolean;
  readOnly: boolean;
  isPending: boolean;
  onReview: OrganizerEntrantTableProps['onReview'];
  onIntent: OrganizerEntrantTableProps['onIntent'];
}) {
  if (readOnly) return null;

  const canApprove = entry.status === 'PENDING' || entry.status === 'RESERVE';
  const canReserve = entry.status === 'PENDING';
  const canWithdraw = entry.status === 'APPROVED' || entry.status === 'RESERVE';
  const mergeSources = entry.members.filter((member) => !member.linkedAccount);

  return (
    <>
      {canApprove ? (
        <Button
          size="sm"
          leftIcon={<Check className="h-4 w-4" />}
          isLoading={isPending}
          onClick={() => onReview(entry, 'approve')}
        >
          Approve
        </Button>
      ) : null}
      {canReserve ? (
        <Button size="sm" variant="outline" isLoading={isPending} onClick={() => onReview(entry, 'reserve')}>
          Reserve
        </Button>
      ) : null}
      {(entry.status === 'PENDING' || canWithdraw || canAdmin) ? (
        <details className="relative">
          <summary
            aria-label={`More actions for ${entry.members.map((member) => member.displayName).join(' and ')}`}
            title="More actions"
            className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-lg text-text-muted hover:bg-surface-elevated hover:text-text"
          >
            <MoreHorizontal className="h-4 w-4" />
          </summary>
          <div className="absolute right-0 z-20 mt-1 w-48 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-lg)]">
            {entry.status === 'PENDING' || entry.status === 'RESERVE' ? (
              <MenuButton icon={<UserMinus className="h-4 w-4" />} label="Decline" onClick={() => onIntent({ kind: 'decline', entry })} />
            ) : null}
            {canWithdraw ? (
              <MenuButton icon={<UserMinus className="h-4 w-4" />} label="Withdraw entry" onClick={() => onIntent({ kind: 'withdraw', entry })} />
            ) : null}
            {canAdmin && canWithdraw ? (
              <MenuButton icon={<UserCheck className="h-4 w-4" />} label="Replace entrant" onClick={() => onIntent({ kind: 'replace', entry })} />
            ) : null}
            {canAdmin && mergeSources.map((source) => (
              <MenuButton
                key={source.entrantPublicId}
                icon={<UsersRound className="h-4 w-4" />}
                label="Merge duplicate"
                onClick={() => onIntent({ kind: 'merge', entry, source })}
              />
            ))}
          </div>
        </details>
      ) : null}
    </>
  );
}

function MenuButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-text hover:bg-surface-elevated"
      onClick={(event) => {
        event.currentTarget.closest('details')?.removeAttribute('open');
        onClick();
      }}
    >
      <span className="text-text-muted">{icon}</span>
      {label}
    </button>
  );
}
